import {
	BookOpenIcon,
	CircleDotIcon,
	LayoutGridIcon,
	PlusIcon,
	PinIcon,
	PinOffIcon,
	XIcon
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Button, TooltipButton } from "~/components/ui/button"
import { useWorkspace } from "~/components/workspace/Workspace"
import {
	ContextMenu,
	ContextMenuTrigger,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator
} from "~/components/ui/context-menu"
import { cn } from "~/lib/utils"
import { TutorialDiscovery } from "~/components/tutorials/TutorialDiscovery"
import { useTutorial } from "~/hooks/useTutorial"

export function RouteTabs() {
	const workspace = useWorkspace()
	return workspace ? <WorkspaceTabs workspace={workspace} /> : null
}

function WorkspaceTabs({
	workspace
}: {
	workspace: NonNullable<ReturnType<typeof useWorkspace>>
}) {
	const {
		tabs,
		activeId,
		dirtyTabIds,
		select,
		close,
		open,
		isMobile,
		togglePin,
		reorder
	} = workspace
	const [draggedId, setDraggedId] = useState<string | null>(null)
	const [dropTarget, setDropTarget] = useState<{
		id: string
		after: boolean
	} | null>(null)
	const { scope } = useTutorial<HTMLElement>("tutorial-demo", {
		automatic: false,
		enabled: !isMobile
	})
	const activeButton = useRef<HTMLButtonElement>(null)
	useEffect(() => {
		activeButton.current?.scrollIntoView({
			block: "nearest",
			inline: "nearest"
		})
	}, [activeId, tabs.length, isMobile])

	if (isMobile) return null

	return (
		<nav
			ref={scope}
			aria-label="Workspace"
			className="flex min-w-0 items-center gap-2"
		>
			<div
				role="tablist"
				aria-label="Workspace tabs"
				data-tutorial="workspace-tabs"
				className="no-scrollbar flex min-w-0 items-center gap-2 overflow-x-auto overscroll-x-contain py-1 pr-1"
				onKeyDown={(event) => {
					if (
						event.defaultPrevented ||
						!(event.target instanceof Element) ||
						event.target.getAttribute("role") !== "tab"
					)
						return
					if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
						return
					event.preventDefault()
					const focusedId = event.target.id
					const index = tabs.findIndex(
						(tab) => `workspace-tab-${tab.id}` === focusedId
					)
					if (index < 0) return
					if (event.altKey && ["ArrowLeft", "ArrowRight"].includes(event.key)) {
						const target = tabs[index + (event.key === "ArrowRight" ? 1 : -1)]
						if (target)
							reorder(tabs[index]!.id, target.id, event.key === "ArrowRight")
						return
					}
					const nextIndex =
						event.key === "Home"
							? 0
							: event.key === "End"
								? tabs.length - 1
								: (index +
										(event.key === "ArrowRight" ? 1 : -1) +
										tabs.length) %
									tabs.length
					const next = tabs[nextIndex]
					if (next) {
						select(next.id)
						document.getElementById(`workspace-tab-${next.id}`)?.focus()
					}
				}}
			>
				{tabs.map((tab) => {
					const active = tab.id === activeId
					const dirty = dirtyTabIds.includes(tab.id)
					const Icon =
						tab.kind === "issue"
							? CircleDotIcon
							: tab.kind === "document"
								? BookOpenIcon
								: LayoutGridIcon
					return (
						<ContextMenu key={tab.id}>
							<ContextMenuTrigger
								draggable
								onDragStart={(event) => {
									event.dataTransfer.effectAllowed = "move"
									event.dataTransfer.setData("text/plain", tab.id)
									setDraggedId(tab.id)
								}}
								onDragOver={(event) => {
									const dragged = tabs.find((entry) => entry.id === draggedId)
									if (
										!dragged ||
										dragged.id === tab.id ||
										!!dragged.pinned !== !!tab.pinned
									)
										return
									event.preventDefault()
									event.dataTransfer.dropEffect = "move"
									const rect = event.currentTarget.getBoundingClientRect()
									setDropTarget({
										id: tab.id,
										after: event.clientX > rect.left + rect.width / 2
									})
								}}
								onDragLeave={(event) => {
									if (
										!(event.relatedTarget instanceof Node) ||
										!event.currentTarget.contains(event.relatedTarget)
									)
										setDropTarget(null)
								}}
								onDrop={(event) => {
									if (!draggedId) return
									event.preventDefault()
									const rect = event.currentTarget.getBoundingClientRect()
									reorder(
										draggedId,
										tab.id,
										event.clientX > rect.left + rect.width / 2
									)
									setDraggedId(null)
									setDropTarget(null)
								}}
								onDragEnd={() => {
									setDraggedId(null)
									setDropTarget(null)
								}}
								onKeyDown={(event) => {
									if (
										event.key !== "ContextMenu" &&
										!(event.shiftKey && event.key === "F10")
									)
										return
									event.preventDefault()
									const rect = event.currentTarget.getBoundingClientRect()
									event.currentTarget.dispatchEvent(
										new MouseEvent("contextmenu", {
											bubbles: true,
											cancelable: true,
											clientX: rect.left,
											clientY: rect.bottom
										})
									)
								}}
								className={cn(
									"group/route-tab control-pressable relative flex h-9 min-w-36 max-w-60 shrink-0 items-center gap-1 rounded-lg px-1 text-text-sub hover:bg-background-elevated",
									draggedId === tab.id && "opacity-50",
									dropTarget?.id === tab.id &&
										(dropTarget.after
											? "after:absolute after:inset-y-0 after:-right-1 after:w-0.5 after:bg-orbit"
											: "before:absolute before:inset-y-0 before:-left-1 before:w-0.5 before:bg-orbit"),
									active
										? "surface-raised bg-background-elevated text-text-main"
										: "control-raised bg-background-secondary"
								)}
								onMouseDown={(event) => {
									if (event.button === 1) event.preventDefault()
								}}
								onAuxClick={(event) => {
									if (event.button === 1) {
										event.preventDefault()
										void close(tab.id)
									}
								}}
							>
								<Button
									ref={active ? activeButton : undefined}
									role="tab"
									id={`workspace-tab-${tab.id}`}
									aria-controls={`workspace-panel-${tab.id}`}
									aria-selected={active}
									tabIndex={active ? 0 : -1}
									variant="none"
									size="none"
									className={cn(
										"h-full min-w-0 flex-1 justify-start px-2 font-normal",
										active && "font-medium"
									)}
									title={`${tab.title}${tab.pinned ? " (pinned)" : ""}`}
									aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
									onClick={() => select(tab.id)}
								>
									<Icon
										className={cn(
											"size-4",
											active ? "text-orbit" : "text-icons"
										)}
									/>
									{tab.pinned && (
										<PinIcon className="size-3" aria-label="Pinned tab" />
									)}
									<span className="truncate">{tab.title}</span>
									{dirty && (
										<span
											className="size-1.5 shrink-0 rounded-full bg-orbit"
											role="img"
											aria-label="Unsaved changes"
										/>
									)}
								</Button>
								{!tab.pinned && (
									<Button
										variant="ghost"
										size="icon-sm"
										className={cn(
											"shrink-0",
											!active &&
												"md:opacity-0 md:group-hover/route-tab:opacity-100 md:group-focus-within/route-tab:opacity-100"
										)}
										aria-label={`Close ${tab.title}`}
										onClick={() => void close(tab.id)}
									>
										<XIcon className="size-3.5" />
									</Button>
								)}
							</ContextMenuTrigger>
							<ContextMenuContent>
								<ContextMenuItem onClick={() => togglePin(tab.id)}>
									{tab.pinned ? <PinOffIcon /> : <PinIcon />}
									{tab.pinned ? "Unpin tab" : "Pin tab"}
								</ContextMenuItem>
								<ContextMenuSeparator />
								<ContextMenuItem
									disabled={!!tab.pinned}
									onClick={() => void close(tab.id)}
								>
									Close tab
								</ContextMenuItem>
								<ContextMenuItem
									disabled={
										!tabs.some((entry) => entry.id !== tab.id && !entry.pinned)
									}
									onClick={() => void close(tab.id, "others")}
								>
									Close other tabs
								</ContextMenuItem>
								<ContextMenuItem
									disabled={!tabs.some((entry) => !entry.pinned)}
									onClick={() => void close(tab.id, "all")}
								>
									Close all tabs
								</ContextMenuItem>
							</ContextMenuContent>
						</ContextMenu>
					)
				})}
			</div>
			<TutorialDiscovery tutorialId="tutorial-demo" automatic>
				<TooltipButton
					variant="outline"
					size="icon"
					tooltip="New tab"
					data-tutorial="workspace-new-tab"
					onClick={() => open()}
				>
					<PlusIcon className="size-4" />
				</TooltipButton>
			</TutorialDiscovery>
		</nav>
	)
}
